// Hero & Footer background color → derived section theme (Task #1652).
//
// The owner picks ONE background color shared by BOTH the hero and the footer
// of the public website. Text (primary + muted), the panel tint, and which
// accent variant stays legible are all DERIVED from the picked color's WCAG
// relative luminance — the owner never picks a text color.
//
// Shared between client (live template) and server (SSR renders the same
// markup), so this module must stay dependency-free and side-effect-free.

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function parseHex(hex: string): [number, number, number] | null {
  if (typeof hex !== "string" || !HEX_RE.test(hex)) return null;
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function toHex(r: number, g: number, b: number): string {
  return (
    "#" + [r, g, b].map((v) => clampByte(v).toString(16).padStart(2, "0")).join("")
  );
}

// Blend an rgb triple toward a target channel value (0 = black, 255 = white).
function mixToward(
  [r, g, b]: [number, number, number],
  target: number,
  amount: number,
): [number, number, number] {
  return [
    r + (target - r) * amount,
    g + (target - g) * amount,
    b + (target - b) * amount,
  ];
}

// WCAG relative luminance (0 = black, 1 = white).
export function relativeLuminanceOfHex(hex: string): number | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const chan = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * chan(rgb[0]) + 0.7152 * chan(rgb[1]) + 0.0722 * chan(rgb[2]);
}

// Black text (#000) and white text (#fff) give mathematically EQUAL WCAG
// contrast at L ≈ 0.179, but on mid-tone backgrounds (warm gray #8A817C,
// sage, mid blues — L ≈ 0.18–0.3) white text reads far better perceptually,
// so the flip point sits above the equal-contrast luminance. 0.30 keeps
// genuinely light picks (cream, white, light grays ≥ ~#AAAAAA) on dark text
// while every mid-tone and darker pick gets white text.
export const LIGHT_BG_LUMINANCE_THRESHOLD = 0.3;

export type HeroFooterTheme = {
  /** The picked background (verbatim). */
  bg: string;
  /** Slightly-elevated surface tint for panels sitting on the background. */
  surface: string;
  /** Primary heading/body text color. */
  text: string;
  /** Muted text (footer links/legal). */
  mutedText: string;
  /** True when the background is light → dark text. */
  isLight: boolean;
};

/**
 * Derive the hero/footer section theme from the picked background hex.
 * Returns null when the value is unset ("") or not a full #rrggbb hex —
 * callers then keep the default black design untouched (byte-identical).
 */
export function deriveHeroFooterTheme(bg: string | null | undefined): HeroFooterTheme | null {
  const rgb = typeof bg === "string" ? parseHex(bg) : null;
  if (!rgb || typeof bg !== "string") return null;
  const luminance = relativeLuminanceOfHex(bg)!;
  const isLight = luminance > LIGHT_BG_LUMINANCE_THRESHOLD;
  // Surface: nudge 4% toward the text side so panels read as gently elevated
  // on any background (black → #0a0a0a, matching the current default surface).
  const surface = toHex(...mixToward(rgb, isLight ? 0 : 255, 0.04));
  return {
    bg,
    surface,
    // Near-black on light backgrounds (matches --theme-near-black); pure white
    // on dark ones (matches the current hero text).
    text: isLight ? "#141414" : "#FFFFFF",
    // Muted: matches the current footer grey on dark; a readable dark grey on
    // light backgrounds.
    mutedText: isLight ? "#3D3D3D" : "#EAEAEA",
    isLight,
  };
}
